import { useEffect, useMemo, useState } from "react";
import client from "@/lib/api";
import { NEWS } from "@/constants/testIds";
import Loader from "@/components/Loader";
import usePageTitle from "@/hooks/usePageTitle";

export default function Newsroom() {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  usePageTitle("Newsroom", "Press coverage of Shri Nitin Narang from verified Indian and international outlets — AICF, business, chess, AI policy and entrepreneurship.");

  useEffect(() => {
    (async () => {
      const [n, c] = await Promise.all([client.get("/news"), client.get("/news/categories")]);
      setItems(n.data.items); setCats(c.data.categories);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    return items.filter(n =>
      (cat === "All" || n.category === cat) &&
      (q === "" || `${n.title} ${n.excerpt} ${n.source}`.toLowerCase().includes(q.toLowerCase()))
    );
  }, [items, cat, q]);

  return (
    <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
      <div className="grid lg:grid-cols-12 gap-12 mb-12">
        <div className="lg:col-span-8">
          <div className="eyebrow mb-4">Newsroom</div>
          <h1 className="serif-title text-5xl lg:text-6xl text-[#0a1628] mb-4">In the news.</h1>
          <p className="text-[#4a4a4a] text-[17px] leading-relaxed max-w-2xl">
            Public coverage of Shri Nitin Narang from verified Indian and international outlets. Each item links to the original source — titles, dates and short excerpts only, as is standard practice for news aggregation.
          </p>
        </div>
        <div className="lg:col-span-4">
          <div className="border hairline p-5 bg-white">
            <label className="label-sm">Search</label>
            <input
              data-testid={NEWS.search}
              className="form-input"
              placeholder="Search by keyword, source…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-10 pb-6 border-b hairline" data-testid={NEWS.filter}>
        {["All", ...cats].map(c => (
          <button key={c}
            data-testid={`news-cat-${c.toLowerCase()}`}
            onClick={() => setCat(c)}
            className={`px-4 py-2 text-[11px] tracking-[0.18em] uppercase border transition-all ${
              cat === c
                ? "bg-[#0a1628] text-[#faf8f3] border-[#0a1628]"
                : "border-[#d9d4c7] text-[#2c2c2c] hover:border-[#0a1628]"
            }`}>{c}</button>
        ))}
      </div>

      {!items ? <Loader label="Loading newsroom" /> : (
        <div className="grid lg:grid-cols-2 gap-6" data-testid={NEWS.list}>
          {filtered.map(n => (
            <a key={n.id}
              href={n.url} target="_blank" rel="noreferrer"
              data-testid={NEWS.item(n.id)}
              className="border hairline bg-white p-7 card-hover block">
              <div className="flex items-center justify-between mb-5">
                <span className="eyebrow text-[#b8975d]">{n.category}</span>
                <span className="text-[11px] text-[#6b6b6b]">{new Date(n.date).toLocaleDateString("en-IN", {month: "short", day: "numeric", year: "numeric"})}</span>
              </div>
              <h3 className="font-display text-2xl text-[#0a1628] mb-3 leading-tight">{n.title}</h3>
              <p className="text-[#4a4a4a] text-[14px] leading-relaxed mb-6">{n.excerpt}</p>
              <div className="flex items-center justify-between pt-4 border-t hairline">
                <span className="text-[11px] tracking-[0.2em] uppercase text-[#6b6b6b]">{n.source}</span>
                <span className="text-[11px] tracking-[0.2em] uppercase text-[#0a1628] flex items-center gap-2">
                  Read on source <i className="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
                </span>
              </div>
            </a>
          ))}
          {filtered.length === 0 && (
            <div className="lg:col-span-2 text-center py-20 text-[#6b6b6b]">
              <i className="fa-regular fa-newspaper text-3xl mb-3 text-[#b8975d]"></i>
              <p>No coverage matches your filters.</p>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
